import { useState } from "react";
import { img } from "../data/images";
import { APS_URL, CONTACT_EMAIL } from "../data/chapters";
import { listProducts } from "../lib/productsApi";
import Button from "../components/Button";
import ImageWithCaption from "../components/ImageWithCaption";
import ImageCardRail from "../components/ImageCardRail";
import Footer from "../components/Footer";
import { TLink } from "../components/TransitionProvider";

const CARDS = [
  {
    image: img("cardPeople"), kicker: "For people", title: "Software that feels human.",
    text: "Websites, apps and automation that are clear enough for anyone, and built to keep the door open for the whole community around them.",
    cta: { label: "Start A Conversation", props: { href: `mailto:${CONTACT_EMAIL}` } },
  },
  {
    image: img("cardTeams"), kicker: "For teams", title: "One team, on your rhythm.",
    text: "Subscription development and automation. Start small, grow when you're ready, and keep the same people beside you as you do.",
    cta: { label: "See The Products", props: { to: "/products", label: "Products" } },
  },
  {
    image: img("cardAps"), kicker: "For regulated work", title: "Built to be trusted.",
    text: "When the work handles sensitive information, our Advanced Platform Services team takes it on, with HIPAA-conscious architecture and SOC 2-aligned operations.",
    cta: { label: "Meet APS", props: { href: APS_URL } },
  },
];

export default function Home() {
  const picks = ["dev-strategy-call", "dev-launchpad", "ai-starter"].map((id) => listProducts().find((p) => p.id === id)).filter(Boolean);
  const [hover, setHover] = useState(null);

  return (
    <>
      {/* ── 01 · Hero: black type on paper, photograph lower right ── */}
      <section className="hero">
        <div className="hero-meta t-cap" data-rv="">
          <span>Digital Billions Group</span>
          <span className="hero-meta-mid">Software for people &amp; community</span>
          <span>Vol. 05 — 2026</span>
        </div>

        <h1 className="hero-title t-display" aria-label="We build with people.">
          <span className="mask"><span className="rise" style={{ "--d": "120ms" }}>We build</span></span>
          <span className="mask"><span className="rise hero-light" style={{ "--d": "260ms" }}>with people.</span></span>
        </h1>

        <div className="hero-text" data-rv="" style={{ "--d": "420ms" }}>
          <p className="t-lg">
            We're a software team that builds alongside the people who'll use the work: tools that are clear to use, safe to trust, and made for the communities they serve.
          </p>
          <div className="hero-actions">
            <Button variant="primary" to="/products" label="Products">Explore What We're Building</Button>
            <Button variant="secondary" href={`mailto:${CONTACT_EMAIL}`}>Start A Conversation</Button>
          </div>
          <p className="hero-aps t-cap">
            Regulated or enterprise work? <a href={APS_URL} target="_blank" rel="noopener noreferrer">Meet Advanced Platform Services ↗</a>
          </p>
        </div>

        <ImageWithCaption
          className="hero-figure"
          image={img("hero")}
          index="Fig. 01"
          title="Room for everyone"
          text="Spaces where people gather are the ones we design for: open, warm, and built to be shared."
          rv={false}
        />
      </section>

      {/* ── 02 · Statement ── */}
      <section className="statement">
        <p className="t-cap" data-rv="">01 — What we believe</p>
        <p className="statement-text t-h1" data-rv="" style={{ "--d": "80ms" }}>
          Software is at its best when it brings people together. We build tools that are easy to trust, and communities that are glad to use them.
        </p>
      </section>

      {/* ── 03 · Narrative: photograph and words as one composition ── */}
      <section className="narr">
        <ImageWithCaption
          className="narr-figure"
          image={img("story")}
          index="Fig. 02"
          title="Made together"
          text="Good work takes shape in layers, with each person who touches it adding something."
        />
        <div className="narr-text">
          <p className="t-cap" data-rv="">02 — How we work</p>
          <h2 className="t-h1" data-rv="" style={{ "--d": "60ms" }}>Built with people, not just for them.</h2>
          <p className="t-body" data-rv="" style={{ "--d": "120ms" }}>
            Every project starts with a conversation: who will use this, who does it need to protect, and what does a good day look like for them? We design and build from there, in the open, alongside the people who will rely on it.
          </p>
          <p className="t-body" data-rv="" style={{ "--d": "180ms" }}>
            It's the difference between software that is delivered and software that is welcomed.
          </p>
        </div>
      </section>

      {/* ── 04 · Three ways in (swipe cards on mobile) ── */}
      <section className="ways">
        <div className="section-head" data-rv="">
          <p className="t-cap">03 — Where we can help</p>
          <h2 className="t-h1">Three ways to start.</h2>
        </div>
        <ImageCardRail cards={CARDS} />
      </section>

      {/* ── 05 · Products preview ── */}
      <section className="preview">
        <div className="section-head section-head--row" data-rv="">
          <div>
            <p className="t-cap">04 — Products</p>
            <h2 className="t-h1">Pick a place to begin.</h2>
          </div>
          <Button variant="secondary" to="/products" label="Products">See All Products</Button>
        </div>
        <ul className="rows" onMouseLeave={() => setHover(null)}>
          {picks.map((p, i) => (
            <li key={p.id} data-rv="" style={{ "--d": `${i * 70}ms` }} className={hover !== null && hover !== i ? "is-dim" : ""} onMouseEnter={() => setHover(i)}>
              <TLink to={`/products/${p.id}`} label={p.name} className="row">
                <span className="row-num t-cap">{p.number}</span>
                <span className="row-name t-h2">{p.name}</span>
                <span className="row-tag t-cap">{p.tag}</span>
                <span className="row-price t-lg">{p.price}<em>{p.cadence}</em></span>
                <span className="row-arrow" aria-hidden="true">→</span>
              </TLink>
            </li>
          ))}
        </ul>
      </section>

      <Footer />
    </>
  );
}
